import { LogFn, LogLine, MetricFn, MetricMeasurement } from "@nova/telemetry"
import { TelemetryPostBody } from "./telemetry-payload"
import { wait, CancellablePromise } from "@nova/promise-wait"

export function setupBrowserTelemetry(
    // collector endpoint
    collectorEndpoint: string,
    // how frequently we'll send to the server
    debounceTime: number,
    // navigator - we use the sendBeacon
    navigator: Navigator,
    // document - we listen to events
    document: Document
): {
    log: LogFn
    metric: MetricFn
} {
    // pending drain operations
    let pendingDrain: CancellablePromise<void> | null = null

    // pending log lines and measurements
    const batchedLogs: LogLine[] = []
    const batchedMeasures: MetricMeasurement[] = []

    function drain() {
        // if we don't have anything pending, return right away
        if (!pendingDrain) return

        // cancel & clear the current drain - because that's what we're doing right now
        pendingDrain.cancel()
        pendingDrain = null

        // send it up to the server
        navigator.sendBeacon(
            collectorEndpoint,
            JSON.stringify({
                logs: batchedLogs.splice(0, batchedLogs.length),
                measures: batchedMeasures.splice(0, batchedMeasures.length),
            } as TelemetryPostBody)
        )
    }

    function scheduleDrain() {
        if (pendingDrain === null) {
            pendingDrain = wait(debounceTime).then(drain)
        }
    }

    const log = (line: LogLine) => {
        batchedLogs.push(line)
        scheduleDrain()
    }

    const metric = (metric: MetricMeasurement) => {
        batchedMeasures.push(metric)
        scheduleDrain()
    }

    // drain when the page is hidden - this happens as part of the page unload
    // but it isn't 100% reliable - just hoping to get some extra log lines
    document.addEventListener("visibilitychange", drain)

    return { log, metric }
}
